import { create } from 'zustand';
import { makeRequest } from 'api/api-client';
import { HTTP } from 'constants/http-method';
import { API_ENDPOINTS } from 'api/endpoints';

// Helper function to merge unique cities
const mergeUniqueCities = (existingCities, newCities) => {
  const cityMap = new Map();
  existingCities.forEach((city) => cityMap.set(city.id, city));
  newCities.forEach((city) => cityMap.set(city.id, city));
  return Array.from(cityMap.values());
};

// Helper function to transform cities into value/label format
const transformCities = (cities) => {
  return cities.map((city) => ({
    value: city.id,
    label: city.name,
    stateId: city.stateId
  }));
};

const useGetCitiesStore = create((set, get) => ({
  cities: { all: [] }, // Store cities globally & by stateId

  getCitiesById: async (stateId) => {
    if (!stateId) return;
    if (get().cities[stateId]) return; // Prevent redundant API calls

    try {
      const response = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_STATE_CITIES,
        null,
        null,
        stateId
      );
      if (response?.data?.success) {
        const transformedCities = transformCities(response.data.data.cities);
        set((state) => {
          const mergedCities = mergeUniqueCities(
            state.cities.all,
            transformedCities
          );
          return {
            cities: {
              ...state.cities,
              [stateId]: transformedCities,
              all: mergedCities
            }
          };
        });
      }
    } finally {
    }
  },

  getAllCities: async () => {
    if (get().cities['all'].length) return; // Prevent unnecessary calls

    try {
      const response = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CITIES,
        null,
        null
      );
      if (response?.data?.success) {
        const transformedCities = transformCities(response.data.data.cities);
        set((state) => ({
          cities: { ...state.cities, all: transformedCities }
        }));
      }
    } finally {
    }
  },

  refetchCitiesById: async (stateId) => {
    if (!stateId) return;

    try {
      const response = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_STATE_CITIES,
        null,
        null,
        stateId
      );
      if (response?.data?.success) {
        const transformedCities = transformCities(response.data.data.cities);
        set((state) => {
          const mergedCities = mergeUniqueCities(
            state.cities.all,
            transformedCities
          );
          return {
            cities: {
              ...state.cities,
              [stateId]: transformedCities,
              all: mergedCities
            }
          };
        });
      }
    } finally {
    }
  },

  refetchAllCities: async () => {
    try {
      const response = await makeRequest(
        HTTP.GET,
        API_ENDPOINTS.GET_CITIES,
        null,
        null
      );
      if (response?.data?.success) {
        const transformedCities = transformCities(response.data.data.cities);
        set((state) => ({
          cities: { ...state.cities, all: transformedCities }
        }));
      }
    } finally {
    }
  },

  resetCities: () => set({ cities: { all: [] } })
}));

export default useGetCitiesStore;
